import React, { useContext } from "react";
import { useDashboardStatistics } from "@shared/features/dashboard/hooks";
import { toFixed } from "@shared/utils/numbers";
import styled, { useTheme } from "styled-components";
import PnlStatisticItem from "features/dashboard/Statistics/PnlStatisticItem";
import MobileStatisticItem from "features/dashboard/MobileDashboard/MobileStatisticItem";
import Flex from "common/Boxes/Flex";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

import { LanguageNameSpaces, TradeData } from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import { SocketPnlContext } from "@shared/features/dashboard/SocketPnl.context";
import { useTotalPnl } from "@shared/features/socket/hooks";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";
import {
  HelperTooltip,
  HelperTooltipProps,
} from "common/Tooltips/HelperTooltip";

const Statistics = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { isNewLook } = useTheme();
  const {
    tradesAmount,
    openTradesData,
    tradesPotentialProfit,
    precision,
    helperProps,
  } = useDashboardStatistics(true, true);

  if (isNewLook)
    return (
      <NewLookStatistics
        tradesAmount={tradesAmount}
        tradesPotentialProfit={tradesPotentialProfit}
        openTradesData={openTradesData}
        precision={precision}
        helperProps={helperProps}
      />
    );

  return (
    <Wrapper>
      <MobileStatisticItem
        title={t("statistics.open_trades_amount")}
        value={toFixed(tradesAmount, precision)}
        helperProps={helperProps}
      />
      <Separator />
      <PnlStatisticItem openTradesData={openTradesData} />
      <Separator />
      <MobileStatisticItem
        title={t("statistics.potential_profit")}
        value={tradesPotentialProfit}
      />
    </Wrapper>
  );
};

export default React.memo(Statistics);

interface NewLookStatisticsProps {
  tradesAmount: number;
  tradesPotentialProfit: number;
  openTradesData: TradeData[];
  precision: number;
  helperProps: HelperTooltipProps;
}

/** New look: three key figures in one tinted panel, label above value. */
const NewLookStatistics = ({
  tradesAmount,
  tradesPotentialProfit,
  openTradesData,
  precision,
  helperProps,
}: NewLookStatisticsProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { socketPnlValue } = useContext(SocketPnlContext) || {};
  const totalPnl = useTotalPnl(openTradesData, socketPnlValue);
  let pnlColor: string | undefined;
  if (totalPnl > 0) pnlColor = theme.colors.green;
  else if (totalPnl < 0) pnlColor = theme.colors.red;

  const money = (value: number) => (
    <TextWithPrecisionAndIcon
      text={value}
      precision={precision}
      iconType={ICON_TYPES.DISPLAY_CURRENCY}
      textStyle={INHERIT_TEXT}
    />
  );

  return (
    <Panel>
      <Figure>
        <FigureLabel>
          {t("statistics.open_trades_amount")}
          <HelperTooltip {...helperProps} containerStyle={TOOLTIP_STYLE} />
        </FigureLabel>
        <FigureValue>{money(tradesAmount)}</FigureValue>
      </Figure>
      <Figure>
        <FigureLabel>{t("statistics.open_trades_pnl_mobile")}</FigureLabel>
        <FigureValue style={{ color: pnlColor }}>{money(totalPnl)}</FigureValue>
      </Figure>
      <Figure>
        <FigureLabel>{t("statistics.potential_profit")}</FigureLabel>
        <FigureValue>{money(tradesPotentialProfit)}</FigureValue>
      </Figure>
    </Panel>
  );
};

const INHERIT_TEXT = { color: "inherit", fontSize: "inherit" };
const TOOLTIP_STYLE = { marginLeft: 4, display: "inline-flex" };

const Panel = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border: ${(p) => p.theme.borders.panel};
  box-shadow: ${(p) => p.theme.shadows.panel};
  border-radius: 14px;
  background: ${(p) => p.theme.colors.cardBgColor};
`;
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 4px;
  min-width: 0;
  padding: 10px 12px;
  &:not(:last-child) {
    border-right: ${(p) => p.theme.borders.lightest};
  }
`;
const FigureLabel = styled.span`
  display: flex;
  align-items: flex-start;
  color: ${(p) => p.theme.colors.secondaryText};
  font-size: 11.5px;
  line-height: 15px;
`;
const FigureValue = styled.span`
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 15px;
  line-height: 20px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Wrapper = styled(Flex)`
  width: 100%;
  align-items: flex-start;
  column-gap: 12px;
`;
const Separator = styled.div`
  border-right: ${(p) => p.theme.borders.light};
  height: 40px;
`;
